import type { InternalMeetingConfigProblem, MeetingModeWire } from '@ai-meeting/shared'
import { normalizeSttConfigContext, type SttConfigContext } from '@/utils/sttConfigContext'

export interface InternalMeetingConfigInput {
  targetLang: string
  meetingId?: string
  userId?: string
  /** Signed-in reader's name. Blank is treated as absent. */
  userName?: string
  /** Present only to be rejected — an internal meeting cannot run natively. */
  nativeMode?: boolean
  /** Present only to be rejected — the server picks the engine for this mode. */
  sttProvider?: string | null
  /** Passed through when non-empty; see the note in {@link buildInternalMeetingConfig}. */
  context?: SttConfigContext | null
}

/**
 * Decide whether a config would be rejected, before opening the socket.
 *
 * The server answers every one of these by closing with code 4005 and **nothing
 * else** — no error frame, no close reason. The explanation exists only in its
 * own log, so a client that learns of the problem from the close code learns
 * that there was one and not which. Checking here is what lets the screen say
 * what is actually wrong.
 *
 * @returns The first problem found, or null when the config is acceptable.
 */
export function validateInternalMeetingConfig(
  input: InternalMeetingConfigInput
): InternalMeetingConfigProblem | null {
  // Not merely required: without it the minutes fall back to Japanese, so a
  // Vietnamese meeting would quietly produce a Japanese document.
  if (!input.targetLang || !input.targetLang.trim()) return 'missing_target_lang'
  if (input.nativeMode) return 'native_mode_not_supported'
  if (input.sttProvider) return 'stt_provider_not_supported'
  return null
}

/**
 * Build the `config` frame for an internal meeting.
 *
 * Deliberately narrow. The realtime path always sends `sttProvider`,
 * `audioFormat`, `sampleRate` and `numChannels`; the first of those is fatal
 * here, and the audio ones describe raw PCM, which is not what this mode sends —
 * omitting them is how the server is told to expect a container instead.
 *
 * `context` rides along when there is one. The protocol note for this mode does
 * not mention the field either way, and it is part of the existing config
 * contract, so dropping it would silently cost internal meetings their glossary.
 *
 * @throws Error when the input would be rejected — callers should have run
 *   {@link validateInternalMeetingConfig} first.
 */
export function buildInternalMeetingConfig(
  input: InternalMeetingConfigInput
): Record<string, unknown> {
  const problem = validateInternalMeetingConfig(input)
  if (problem) throw new Error(`Internal meeting config rejected: ${problem}`)

  const meetingMode: MeetingModeWire = 'internal_meeting'
  const config: Record<string, unknown> = {
    event: 'config',
    meetingMode,
    meetingId: input.meetingId,
    userId: input.userId,
    ...(input.userName?.trim() ? { userName: input.userName.trim() } : {}),
    // `type` is required on the wire — omitting it closes with 4005 and no reason.
    translation: { type: 'one_way', targetLang: input.targetLang.trim() },
  }

  const normalizedContext = normalizeSttConfigContext(input.context)
  if (normalizedContext) config.context = normalizedContext
  return config
}
